/* Collection-only enhancement. Native product links and horizontal scrolling remain the fallback. */
(() => {
  if (customElements.get('ac-collections-index')) return;
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches || document.body.dataset.acMotion === 'reduced';
  const wrap = (value, size) => ((value % size) + size) % size;

  class AcCollectionsLoop extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.viewport = this.querySelector('.ac-collections-index__viewport');
      this.track = this.querySelector('.ac-collections-index__track');
      this.originals = [...this.track.children];
      this.pauses = new Set(['outside']);
      this.position = 0;
      this.expansion = { value: 0 };
      this.hoverIndex = -1;
      this.visible = false;
      const on = (target, type, fn, options = {}) => target.addEventListener(type, fn, {...options, signal:this.abort.signal});
      this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
      on(this.motionQuery, 'change', () => this.setup());
      on(document, 'visibilitychange', () => this.pause('document', document.hidden));
      on(document, 'ac:layer:change', event => this.pause('overlay', event.detail.locked));
      on(this, 'pointerenter', event => { if (event.pointerType === 'mouse') this.pause('hover', true); });
      on(this, 'pointerleave', event => { if (event.pointerType === 'mouse') { this.expand(-1); this.pause('hover', false); } });
      on(this.track, 'pointerover', event => {
        if (event.pointerType !== 'mouse' || this.pointer) return;
        const card = event.target.closest('.ac-collections-index__card');
        if (card) {
          this.expand(Number(card.dataset.index));
        }
      });
      on(this, 'focusin', event => {
        if (!event.target.matches(':focus-visible')) return;
        this.pause('focus', true);
        const item = event.target.closest('.ac-collections-index__card');
        if (this.enhanced && item && !item.hasAttribute('data-clone')) {
          const layout = this.layout.find(entry => entry.item === item);
          if (layout) this.position = this.viewportWidth / 2 - layout.start - layout.width / 2;
          this.viewport.scrollLeft = 0;
          this.render();
        }
      });
      on(this, 'focusout', event => { if (!this.contains(event.relatedTarget)) this.pause('focus', false); });
      on(this.viewport, 'dragstart', event => event.preventDefault());
      on(this.viewport, 'pointerdown', event => {
        if (!this.enhanced || event.button !== 0) return;
        this.inertia?.kill();
        this.pause('inertia', false);
        this.pause('drag', true);
        this.pointer = {id:event.pointerId, x:event.clientX, y:event.clientY, lastX:event.clientX, time:performance.now(), velocity:0, dragged:false};
      });
      on(this.viewport, 'pointermove', event => {
        const pointer = this.pointer;
        if (!pointer || event.pointerId !== pointer.id) return;
        const dx = event.clientX - pointer.x, dy = event.clientY - pointer.y;
        if (!pointer.dragged && (Math.abs(dx) < 10 || Math.abs(dx) < Math.abs(dy) * 1.15)) return;
        if (!pointer.dragged) {
          pointer.dragged = true;
          this.viewport.setPointerCapture(pointer.id);
          this.classList.add('is-dragging');
          this.expand(-1);
        }
        const now = performance.now(), delta = event.clientX - pointer.lastX;
        pointer.velocity = pointer.velocity * .85 + delta / Math.max(now - pointer.time, 1) * .15;
        this.position += delta;
        pointer.lastX = event.clientX;
        pointer.time = now;
        this.render();
        event.preventDefault();
      });
      const release = event => {
        const pointer = this.pointer;
        if (!pointer || event.pointerId !== pointer.id) return;
        if (this.viewport.hasPointerCapture(pointer.id)) this.viewport.releasePointerCapture(pointer.id);
        this.pointer = null;
        this.pause('drag', false);
        this.classList.remove('is-dragging');
        if (!pointer.dragged) return;
        this.suppressUntil = performance.now() + 300;
        const velocity = performance.now() - pointer.time > 120 ? 0 : pointer.velocity;
        this.move(velocity * 450, Math.min(1.1, .25 + Math.abs(velocity) * .9));
      };
      on(window, 'pointerup', release);
      on(window, 'pointercancel', release);
      on(this.viewport, 'click', event => {
        if (performance.now() < (this.suppressUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
      }, {capture:true});
      on(this.viewport, 'keydown', event => {
        if (!this.enhanced || event.target !== this.viewport) return;
        const amount = {ArrowLeft:this.viewportWidth * .85, ArrowRight:-this.viewportWidth * .85, Home:-this.position, End:-this.position - (this.originals.length - 1) * (this.baseWidth + this.gap)}[event.key];
        if (amount === undefined) return;
        event.preventDefault();
        this.move(amount, .65);
      });
      this.intersection = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.pause('outside', !this.visible);
      });
      this.intersection.observe(this);
      this.resize = new ResizeObserver(() => {
        const width = this.viewport.clientWidth;
        if (width > 0 && width !== this.viewportWidth) this.setup();
      });
      this.resize.observe(this.viewport);
      if (!window.gsap && document.readyState !== 'complete') on(document, 'DOMContentLoaded', () => this.setup(), {once:true});
      else this.setup();
    }

    setup() {
      this.reset();
      this.viewportWidth = this.viewport.clientWidth;
      if (!this.viewportWidth || reduced() || !window.gsap) return;
      this.enhanced = true;
      this.baseWidth = this.originals[0].getBoundingClientRect().width;
      this.gap = parseFloat(getComputedStyle(this.track).gap) || 0;
      // Fill short collections without adding duplicate keyboard stops or duplicate IDs.
      const repeats = Math.max(1, Math.ceil((this.viewportWidth + this.baseWidth * 4) / ((this.baseWidth + this.gap) * this.originals.length)));
      for (let repetition = 1; repetition < repeats; repetition++) {
        for (const original of this.originals) {
          const copy = original.cloneNode(true);
          copy.setAttribute('data-clone', '');
          copy.setAttribute('aria-hidden', 'true');
          copy.removeAttribute('id');
          for (const node of copy.querySelectorAll('[id]')) node.removeAttribute('id');
          for (const link of copy.querySelectorAll('a')) link.tabIndex = -1;
          this.track.append(copy);
        }
      }
      this.items = [...this.track.children];
      for (let index = 0; index < this.items.length; index++) this.items[index].dataset.index = String(index);
      this.classList.add('is-looping');
      this.viewport.scrollLeft = 0;
      this.render();
      this.startTick();
    }

    pause(reason, value) {
      const wasPaused = this.pauses.size > 0;
      if (value) this.pauses.add(reason); else this.pauses.delete(reason);
      const paused = this.pauses.size > 0;
      this.toggleAttribute('data-paused', paused);
      if (paused) {
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.lastTime = 0;
      } else if (wasPaused) this.startTick();
    }

    expand(index) {
      if (!this.enhanced || index === this.hoverIndex) return;
      this.hoverTween?.kill();
      if (index >= 0) { this.hoverIndex = index; this.expansion.value = 0; }
      for (const item of this.items) item.classList.toggle('is-expanded', index >= 0 && Number(item.dataset.index) === index);
      this.hoverTween = window.gsap.to(this.expansion, {value:index < 0 ? 0 : 1, duration:index < 0 ? .5 : .25, ease:index < 0 ? 'power3.out' : 'power1.out', onUpdate:() => this.render(), onComplete:() => { if (index < 0) this.hoverIndex = -1; }});
    }

    move(amount, duration) {
      this.inertia?.kill();
      this.pause('inertia', true);
      const proxy = {value:this.position};
      this.inertia = window.gsap.to(proxy, {value:proxy.value + amount, duration, ease:'power3.out', onUpdate:() => { this.position = proxy.value; this.render(); }, onComplete:() => this.pause('inertia', false)});
    }

    render() {
      if (!this.enhanced) return;
      // Autoplay spends almost all its frames at the original card width. Keep
      // that geometry and the width writes out of the per-frame path.
      if (this.hoverIndex < 0 && this.expansion.value === 0) {
        const width = this.baseWidth, step = width + this.gap;
        if (!this.staticLayoutReady) {
          this.layout = this.items.map((item, index) => ({item, start:index * step, width}));
          this.cycle = step * this.items.length;
          const widthValue = `${width}px`;
          for (const item of this.items) if (item.style.width !== widthValue) item.style.width = widthValue;
          this.staticVisible = new Array(this.items.length);
          this.staticLayoutReady = true;
        }
        const cycle = this.cycle;
        this.position = -wrap(-this.position, cycle);
        for (let index = 0; index < this.items.length; index++) {
          const item = this.items[index];
          const x = wrap(index * step + this.position + width, cycle) - width;
          const left = Math.max(0, x), right = Math.min(this.viewportWidth, x + width);
          const visible = right > left;
          // Once a card is fully clipped, its zero-width transform can stay
          // still until the loop brings it back into view.
          if (!visible && this.staticVisible[index] === false) continue;
          item.style.transform = `translateX(${left}px) scaleX(${Math.max(0, right - left) / width})`;
          this.staticVisible[index] = visible;
        }
        return;
      }
      this.staticLayoutReady = false;
      this.staticVisible = null;
      const anchor = this.layout?.find(entry => Number(entry.item.dataset.index) === this.hoverIndex);
      const anchorX = anchor && this.cycle ? wrap(anchor.start + this.position + anchor.width, this.cycle) - anchor.width : null;
      let start = 0;
      this.layout = this.items.map(item => {
        const width = this.baseWidth * (1 + (Number(item.dataset.index) === this.hoverIndex ? this.expansion.value : 0));
        const entry = {item, start, width};
        start += width + this.gap;
        return entry;
      });
      this.cycle = start;
      if (anchorX !== null) {
        const next = this.layout.find(entry => entry.item === anchor.item);
        const nextX = wrap(next.start + this.position + next.width, this.cycle) - next.width;
        this.position += anchorX - nextX;
      }
      // Preserve the hovered card's left edge as widths change, including after full loops or drag.
      this.position = -wrap(-this.position, this.cycle);
      // All geometry is cached or calculated before writes; no per-card layout reads in the animation loop.
      for (const {item, start:offset, width} of this.layout) {
        const x = wrap(offset + this.position + width, this.cycle) - width;
        const left = Math.max(0, x), right = Math.min(this.viewportWidth, x + width);
        const scale = Math.max(0, right - left) / width;
        const widthValue = `${width}px`;
        if (item.style.width !== widthValue) item.style.width = widthValue;
        item.style.transform = `translateX(${left}px) scaleX(${scale})`;
      }
    }

    startTick() {
      if (this.frame || !this.enhanced || this.dataset.autoplay !== 'true' || this.pauses.size) return;
      this.lastTime = 0;
      this.frame = requestAnimationFrame(time => this.tick(time));
    }

    tick(time) {
      this.frame = 0;
      if (!this.enhanced || this.pauses.size) return;
      if (this.lastTime) {
        this.position -= Math.min((time - this.lastTime) / 1000, .1) * (200 / 3);
        this.render();
      }
      this.lastTime = time;
      this.frame = requestAnimationFrame(next => this.tick(next));
    }

    reset() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.inertia?.kill();
      this.hoverTween?.kill();
      this.enhanced = false;
      this.pointer = null;
      this.pauses?.delete('inertia');
      this.pauses?.delete('drag');
      this.hoverIndex = -1;
      this.staticLayoutReady = false;
      this.staticVisible = null;
      if (this.expansion) this.expansion.value = 0;
      this.classList.remove('is-looping', 'is-dragging');
      for (const copy of this.track.querySelectorAll('[data-clone]')) copy.remove();
      for (const item of this.originals) { item.style.removeProperty('width'); item.style.removeProperty('transform'); item.classList.remove('is-expanded'); delete item.dataset.index; }
      this.lastTime = 0;
    }

    disconnectedCallback() {
      this.reset();
      this.abort?.abort();
      this.abort = null;
      this.resize?.disconnect();
      this.intersection?.disconnect();
    }
  }

  class AcCollectionsIndex extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.rows = [...this.querySelectorAll('[data-collection-row]')];
      this.more = this.querySelector('.ac-collections-index__more');
      this.animations = new Set();
      this.textReveals = new Set();
      this.rowAnimations = new Map();
      this.revealedRows = new Set();
      if (this.more) {
        this.more.hidden = false;
        this.more.setAttribute('aria-expanded', 'false');
        this.more.querySelector('.ac-action__label').textContent = `[ ${this.more.dataset.moreLabel} ]`;
        this.rows.slice(4).forEach(row => { row.hidden = true; });
        this.more.addEventListener('click', () => {
          const expanded = this.more.getAttribute('aria-expanded') !== 'true';
          this.more.setAttribute('aria-expanded', String(expanded));
          this.more.querySelector('.ac-action__label').textContent = `[ ${expanded ? this.more.dataset.lessLabel : this.more.dataset.moreLabel} ]`;
          this.rows.slice(4).forEach(row => { row.hidden = !expanded; });

          if (expanded) this.rows[4].querySelector('h2 a')?.focus({preventScroll:true});
          else this.more.scrollIntoView({block:'center', behavior:'instant'});
          window.ScrollTrigger?.refresh();
        }, {signal:this.abort.signal});
      }
      this.revealMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const refreshMotion = () => {
        this.reveal?.disconnect();
        this.animations.forEach(animation => animation.cancel());
        this.animations.clear();
        this.rowAnimations.clear();
        this.finishTextReveals();
        if (!reduced()) this.prepareReveals();
      };
      this.revealMotion.addEventListener('change', refreshMotion, {signal:this.abort.signal});
      this.motionSettings = new MutationObserver(() => {
        refreshMotion();
        for (const loop of this.querySelectorAll('ac-collections-loop')) loop.setup();
      });
      this.motionSettings.observe(document.body, {attributes:true,attributeFilter:['data-ac-motion']});
      window.addEventListener('resize', () => {
        this.finishTextReveals();
        this.observeReveals();
      }, {signal:this.abort.signal});
      this.addEventListener('focusin', event => {
        const row = event.target.closest('[data-collection-row]');
        if (row) this.playReveal(row);
      }, {signal:this.abort.signal});
      this.prepareReveals();
      this.revealHeading();
    }

    revealHeading() {
      const heading = this.querySelector('.ac-collections-index__heading');
      if (!heading || reduced()) return;
      const animation = heading.animate([
        {opacity:0,transform:'translateY(28px) scaleY(0)'},
        {opacity:1,transform:'translateY(0) scaleY(1)'}
      ], {duration:700,delay:400,easing:'cubic-bezier(.75,0,.25,1)',fill:'both'});
      animation.pause();
      animation.currentTime = 0;
      this.animations.add(animation);
      const signal = this.abort.signal;
      document.fonts.ready.then(() => {
        if (!signal.aborted && this.isConnected && !reduced()) animation.play();
      });
      animation.finished.catch(() => {}).finally(() => {animation.cancel();this.animations.delete(animation);});
    }

    finishTextReveals() {
      for (const reveal of this.textReveals) reveal.finish();
    }

    measureScrambleText(element, lastLine) {
      if (!element || !element.getClientRects().length || reduced()) return;
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      const nodes = [];
      const range = document.createRange();
      const rectFor = (node, character) => {
        range.setStart(node, character.offset);
        range.setEnd(node, character.offset + character.text.length);
        return range.getBoundingClientRect();
      };
      let node;
      while ((node = walker.nextNode())) {
        if (node.parentElement.closest('script,style,svg,[aria-hidden="true"]')) continue;
        const characters = [];
        let offset = 0;
        for (const text of Array.from(node.data)) {
          if (text.trim()) characters.push({offset,text});
          offset += text.length;
        }
        if (!characters.length) continue;
        const color = getComputedStyle(node.parentElement).color;
        if (!lastLine) {
          const visible = characters.filter(character => {
            const rect = rectFor(node, character);
            return rect.width && rect.height;
          });
          if (visible.length) nodes.push({node,characters:visible,color});
          continue;
        }
        for (let index = characters.length - 1; index >= 0; index--) {
          const rect = rectFor(node, characters[index]);
          if (!rect.width || !rect.height) continue;
          nodes.push({node,characters,color,bottom:rect.bottom});
          break;
        }
      }
      if (!lastLine || !nodes.length) return nodes;
      const bottom = Math.max(...nodes.map(entry => entry.bottom));
      return nodes.map(entry => {
        if (entry.bottom < bottom - 2) return {...entry,characters:[]};
        // Text flows in DOM order. Locate the first character on the last
        // painted line with logarithmic Range reads, then keep that suffix.
        let low = 0, high = entry.characters.length;
        while (low < high) {
          const middle = (low + high) >> 1;
          const rect = rectFor(entry.node, entry.characters[middle]);
          if (rect.width && rect.height && rect.bottom >= bottom - 2) high = middle;
          else low = middle + 1;
        }
        return {...entry,characters:entry.characters.slice(low)};
      });
    }

    scrambleText(element, lastLine, delay, measured) {
      const nodes = measured || this.measureScrambleText(element, lastLine);
      if (!nodes?.length || reduced()) return;
      const replacements = [], characters = [];
      for (const entry of nodes) {
        const selected = entry.characters;
        if (!selected.length) continue;
        const wrapper = document.createElement('span');
        wrapper.className = 'ac-collections-index__scramble';
        const accessible = document.createElement('span');
        accessible.className = 'visually-hidden';
        accessible.textContent = entry.node.data;
        const visual = document.createElement('span');
        visual.setAttribute('aria-hidden','true');
        let offset = 0;
        for (const character of selected) {
          visual.append(document.createTextNode(entry.node.data.slice(offset,character.offset)));
          const glyph = document.createElement('span');
          glyph.className = 'ac-collections-index__character';
          glyph.textContent = character.text;
          glyph.dataset.acScrambleChar = '';
          glyph.style.setProperty('--ac-scramble-ink',entry.color);
          visual.append(glyph);
          characters.push({glyph,text:character.text});
          offset = character.offset + character.text.length;
        }
        visual.append(document.createTextNode(entry.node.data.slice(offset)));
        wrapper.append(accessible,visual);
        entry.node.replaceWith(wrapper);
        replacements.push({wrapper,node:entry.node});
      }
      if (!characters.length) return;
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!?@#$%&*+-=/';
      const start = performance.now() + delay;
      const reveal = {frame:0,finish:() => {
        cancelAnimationFrame(reveal.frame);
        for (const {wrapper,node:original} of replacements) wrapper.replaceWith(original);
        this.textReveals.delete(reveal);
      }};
      const tick = time => {
        if (!this.isConnected || reduced() || time-start>=characters.length*100+200) {reveal.finish();return;}
        for (let index=0;index<characters.length;index++) {
          const {glyph,text} = characters[index];
          const elapsed = time-start-index*100;
          const value = elapsed<0 ? '' : elapsed>=100 ? text : alphabet[Math.floor(Math.random()*alphabet.length)];
          if (glyph.dataset.acScrambleChar !== value) glyph.dataset.acScrambleChar = value;
        }
        reveal.frame = requestAnimationFrame(tick);
      };
      this.textReveals.add(reveal);
      reveal.frame = requestAnimationFrame(tick);
    }

    prepareReveals() {
      if (reduced()) return;
      this.rows.forEach((row, index) => {
        if (this.revealedRows.has(row) || this.rowAnimations.has(row)) return;
        const animations = [];
        const create = (element, frames, options) => {
          if (!element) return;
          const animation = element.animate(frames,{...options,fill:'both'});
          animation.pause();
          animation.currentTime = 0;
          animations.push(animation);
          this.animations.add(animation);
          animation.finished.catch(() => {}).finally(() => { animation.cancel(); this.animations.delete(animation); });
        };
        const delay = index === 0 ? 400 : 0;
        create(row.querySelector('h2'),[{transform:'scaleY(0)'},{transform:'scaleY(1)'}],{
          duration:index < 4 ? 700 : 1100,delay:delay + 200,
          easing:index < 4 ? 'cubic-bezier(.75,0,.25,1)' : 'cubic-bezier(.333333,1,.666667,1)'
        });
        for (const [selector,y] of [['.ac-collections-index__loop',20],['.ac-collections-index__description',40],['.ac-collections-index__count',24],['.ac-collections-index__action',24]]) {
          create(row.querySelector(selector),[{opacity:0,transform:`translateY(${y}px)`},{opacity:1,transform:'translateY(0)'}],{duration:1100,delay,easing:'cubic-bezier(.333333,1,.666667,1)'});
        }
        this.rowAnimations.set(row,animations);
      });
      const signal = this.abort.signal;
      document.fonts.ready.then(() => { if (!signal.aborted && this.isConnected) this.observeReveals(); });
    }

    observeReveals() {
      this.reveal?.disconnect();
      if (reduced()) return;
      this.reveal = new IntersectionObserver(entries => {
        for (const entry of entries) if (entry.isIntersecting && !entry.target.hidden) this.playReveal(entry.target);
      }, {rootMargin:`0px 0px -${innerHeight * .1}px 0px`});
      for (const row of this.rowAnimations.keys()) this.reveal.observe(row);
    }

    playReveal(row) {
      const animations = this.rowAnimations.get(row);
      if (!animations || row.hidden) return;
      this.reveal?.unobserve(row);
      this.rowAnimations.delete(row);
      this.revealedRows.add(row);
      const delay = row === this.rows[0] ? 400 : 0;
      const targets = [[row.querySelector('.ac-collections-index__count'),false]];
      if (innerWidth >= 768) {
        for (const column of row.querySelectorAll('.ac-collections-index__description > div')) targets.push([column,true]);
      }
      // Measure every text target before animation starts or any wrapper is
      // inserted, so subsequent Range reads do not flush earlier DOM writes.
      const measured = targets.map(([element,lastLine]) => this.measureScrambleText(element,lastLine));
      for (const animation of animations) animation.play();
      targets.forEach(([element,lastLine],index) => this.scrambleText(element,lastLine,delay,measured[index]));
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.abort = null;
      this.reveal?.disconnect();

      this.motionSettings?.disconnect();
      this.finishTextReveals();
      this.animations?.forEach(animation => animation.cancel());
      this.animations?.clear();
      this.rowAnimations?.clear();
      this.rows?.forEach(row => { row.hidden = false; });
      if (this.more) this.more.hidden = true;
    }
  }
  customElements.define('ac-collections-loop', AcCollectionsLoop);
  customElements.define('ac-collections-index', AcCollectionsIndex);
})();
